import { initializeApp, getApps } from 'firebase/app'
import { getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging'
import store from '../../../store'
import { eventBus } from '@eventBus'
import api from '@api'

const STORAGE_KEY = 'fcm_device_token'
const VAPID_KEY   = 'BOBLF_6QWB7WDs11V1Ae4q32zn5LbjQAD1xIG_ikZtf5K5Iw01AAfvMOlWrh8JGY1r7QajQSd7zzHn6AR_cBEkk'

const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyDGo2OzBgrtg0nZt5vkCUhbDwBQbqnZUzU',
    authDomain:        'fleetsoft-driver-main.firebaseapp.com',
    projectId:         'fleetsoft-driver-main',
    messagingSenderId: '373505427090',
    appId:             '1:373505427090:web:f6e197c302df184a2ac84b',
}

let _messaging = null

// ─── Permission ──────────────────────────────────────────────────────────────

// Пуш-сповіщення чату доступні лише за наявності права quick_chat_management.
// Якщо права немає — токен не реєструємо (і відкликаємо наявний), тож бекенд
// фізично не має куди слати пуші для цього користувача.
function _hasChatPermission() {
    const perm = store.getters.perm
    return !!(perm && perm.quick_chat_management)
}

// ─── Firebase init ─────────────────────────────────────────────────────────

function _initFirebase() {
    const app = getApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getApps()[0]
    return getMessaging(app)
}

// ─── Token ─────────────────────────────────────────────────────────────────

async function _registerToken() {
    if (!_messaging) return
    try {
        // Delete before getToken to guarantee a fresh token from FCM on every call.
        // Without this, Firebase returns a cached token from IndexedDB even after
        // page reload (e.g. after logout + window.location.reload()).
        try { await deleteToken(_messaging) } catch (_) { /* no cached token to delete */ }

        await navigator.serviceWorker.register('/firebase-messaging-sw.js', { updateViaCache: 'none' })
        const sw    = await navigator.serviceWorker.ready
        const token = await getToken(_messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: sw })
        if (!token) return

        localStorage.setItem(STORAGE_KEY, token)
        await api.newChat.registerFcmToken({ token })
    } catch (e) {
        console.error('[FCM] Token registration failed:', e)
    }
}

export async function requestPermissionAndRegister() {
    // Немає права на чат — не запитуємо навіть дозвіл браузера й відкликаємо токен.
    if (!_hasChatPermission()) {
        await revokeToken()
        return
    }
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return
    try {
        const permission = await Notification.requestPermission()
        store.dispatch('newChat/setFcmPermission', permission)
        if (permission !== 'granted') {
            console.warn('[FCM] Notification permission denied.')
            _watchPermissionChange()
            return
        }
        await _registerToken()
    } catch (e) {
        console.error('[FCM] requestPermission failed:', e)
    }
}

// Слухає зміну дозволу в налаштуваннях браузера (без перезавантаження сторінки).
// Викликається якщо юзер відхилив діалог — щоб автоматично підхопити
// коли він вручну увімкне сповіщення через 🔒 в адресному рядку.
let _permissionStatus = null
function _watchPermissionChange() {
    if (!('permissions' in navigator) || _permissionStatus) return
    navigator.permissions.query({ name: 'notifications' }).then((status) => {
        _permissionStatus = status
        status.onchange = () => {
            store.dispatch('newChat/setFcmPermission', status.state)
            if (status.state === 'granted') {
                _permissionStatus = null
                status.onchange = null
                _registerToken()
            }
        }
    })
}

export async function revokeToken() {
    localStorage.removeItem(STORAGE_KEY)
    if (!_messaging) return
    try {
        await deleteToken(_messaging)
    } catch (_) { /* ignore — token may already be invalid */ }
}

// ─── Foreground listener ───────────────────────────────────────────────────

// ─── Sound ─────────────────────────────────────────────────────────────────

const SOUND_STORAGE_KEY = 'nc_sound_enabled'

function _isSoundEnabled() {
    return localStorage.getItem(SOUND_STORAGE_KEY) !== 'false'
}

let _audioCtx = null

function _getAudioCtx() {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    if (_audioCtx.state === 'suspended') _audioCtx.resume()
    return _audioCtx
}

function _playTone(ctx, freq, delay, dur, volume, type) {
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = type
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0, ctx.currentTime + delay)
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + dur)
    osc.start(ctx.currentTime + delay)
    osc.stop(ctx.currentTime + delay + dur + 0.01)
}

// Личное сообщение — двухнотный восходящий чайм: A5 → C#6
function _playSound() {
    try {
        const ctx = _getAudioCtx()
        ;[[880, 0, 0.12], [1100, 0.1, 0.2]].forEach(([freq, delay, dur]) => {
            _playTone(ctx, freq, delay, dur, 0.25, 'sine')
        })
    } catch (_) { /* audio unavailable — ignore */ }
}

// Новый чат — трёхнотный арпеджио E5 → A5 → C#6, triangle (более мягкий, но заметный)
function _playNewChatSound() {
    try {
        const ctx = _getAudioCtx()
        ;[[660, 0, 0.15], [880, 0.13, 0.15], [1100, 0.26, 0.25]].forEach(([freq, delay, dur]) => {
            _playTone(ctx, freq, delay, dur, 0.3, 'triangle')
        })
    } catch (_) { /* audio unavailable — ignore */ }
}

// ─── Event handlers ────────────────────────────────────────────────────────

function _refreshMessages(chatId) {
    api.newChat.getMessages(chatId, 1)
        .then(response => {
            const msgs = Array.isArray(response) ? response : (response.messages || [])
            store.dispatch('newChat/setMessages', msgs)
        })
        .catch(err => console.error('[FCM] refresh messages error:', err))
}

function _refreshChatList() {
    const type   = store.getters['newChat/activeTab']
    const source = store.getters['newChat/activeSource']
    api.newChat.getList({ type, source })
        .then(response => {
            store.dispatch('newChat/setList', Array.isArray(response) ? response : [])
        })
        .catch(err => console.error('[FCM] refresh chat list error:', err))
}

function _refreshCounter() {
    api.newChat.getTotalCounter()
        .then(response => {
            store.dispatch('newChat/setTotalUnread', (response.total_unread_my_chats_messages || 0) + (response.total_unread_new_chats_messages || 0))
            store.dispatch('newChat/setUnreadMyChats', response.total_unread_my_chats_messages || 0)
        })
        .catch(() => {})
}

function _handleForegroundMessage(payload) {
    if (!store.getters.isLoggedIn) return
    const workspaceEvent = payload.data?.event === 'operator_workspace_changed'
    const workspaceOpen = window.location.pathname.replace(/\/$/, '') === '/operator-workspace'
    if (workspaceEvent || workspaceOpen) {
        const eventId = payload.data?.event_id
        if (eventId && _workspaceSeen.has(eventId)) return
        if (eventId) {
            _workspaceSeen.add(eventId)
            if (_workspaceSeen.size > 500) _workspaceSeen.delete(_workspaceSeen.values().next().value)
        }
        if (!workspaceOpen && ((workspaceEvent && payload.data?.notify === '1') || (!workspaceEvent && ['personal_message', 'new_chat', 'common_list_new_message'].includes(payload.data?.event)))) {
            eventBus.$emit('operator-workspace-attention')
        }
        eventBus.$emit('operator-workspace-event', payload.data || {})
        return
    }

    const { event, chat_id } = payload.data ?? {}

    if (event === 'personal_message' && chat_id) {
        const activeChat = store.getters['newChat/activeChat']
        if (_isSoundEnabled()) _playSound()
        _refreshCounter()

        if (activeChat && String(activeChat.id) === String(chat_id)) {
            _refreshMessages(chat_id)
        } else if (!activeChat) {
            _refreshChatList()
        }
    }

    if (event === 'new_chat') {
        if (_isSoundEnabled()) _playNewChatSound()
        _refreshChatList()
        _refreshCounter()
    }

    if (event === 'common_list_new_message' && chat_id) {
        const activeChat = store.getters['newChat/activeChat']
        if (_isSoundEnabled()) _playSound()
        _refreshCounter()
        _refreshChatList()

        if (activeChat && String(activeChat.id) === String(chat_id)) {
            _refreshMessages(chat_id)
        }
    }

    if (event === 'assign' && chat_id) {
        const activeChat = store.getters['newChat/activeChat']
        _refreshChatList()
        _refreshCounter()

        if (activeChat && String(activeChat.id) === String(chat_id)) {
            _refreshMessages(chat_id)
        }
    }
}

const _workspaceSeen = new Set()

function _setupForegroundListener() {
    // Foreground: tab is active
    onMessage(_messaging, _handleForegroundMessage)

    // Background: tab is open but not focused — SW forwards event via postMessage
    navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'FCM_BACKGROUND_MESSAGE') {
            _handleForegroundMessage(event.data.payload)
        }
    })
}

// ─── EventBus hooks ────────────────────────────────────────────────────────

function _setupEventHooks() {
    eventBus.$on('globalLogout', revokeToken)
    eventBus.$on('operator-workspace-attention', () => {
        if (!_isSoundEnabled() || document.hidden || !document.hasFocus()) return
        const play = () => {
            const now = Date.now()
            if (now - Number(localStorage.getItem('ow-last-sound') || 0) < 1500) return
            localStorage.setItem('ow-last-sound', String(now))
            _playSound()
        }
        if (navigator.locks) navigator.locks.request('ow-notification', play).catch(() => {})
        else play()
    })
    // Реєстрацію на зміну компанії свідомо НЕ вішаємо на companyisChanget:
    // подія емітиться до RFRESH_PROFILE, тому perm там ще від старої компанії.
    // Замість цього нижче реактивно стежимо за perm (див. initFcm).
}

// ─── Entry point ───────────────────────────────────────────────────────────

export function initFcm() {
    _messaging = _initFirebase()
    if (!_messaging) return

    if ('Notification' in window) {
        store.dispatch('newChat/setFcmPermission', Notification.permission)
        if (Notification.permission !== 'granted') _watchPermissionChange()
    }

    _setupForegroundListener()
    _setupEventHooks()

    // Єдине джерело правди для (де)реєстрації токена — стежимо за perm залогіненого
    // юзера. Getter повертає новий об'єкт perm при кожному оновленні профілю
    // (логін, RFRESH_PROFILE, зміна компанії), тож колбек спрацьовує коли треба:
    //   • є право quick_chat_management → реєструємо/переприв'язуємо токен;
    //   • немає права або юзер вийшов    → відкликаємо токен, пуші не приходять.
    store.watch(
        (_, getters) => (getters.isLoggedIn ? getters.perm : null),
        (perm) => {
            if (perm && perm.quick_chat_management) {
                requestPermissionAndRegister()
            } else {
                revokeToken()
            }
        },
        { immediate: true }
    )
}
